import { useEffect, useMemo, useRef, useState } from "react";
import ExpertBandFeedback from "../capabilities/expert-band/ExpertBandFeedback";
import ExpertBandReferenceHelp from "../capabilities/expert-band/ExpertBandReferenceHelp";
import AmbiguityDistributionFeedback from "../capabilities/ambiguity-feedback/AmbiguityDistributionFeedback";
import TargetAudioPlayer from "../capabilities/target-playback/TargetAudioPlayer";
import ReferenceAnchorPlayer from "../capabilities/reference-anchors/ReferenceAnchorPlayer";
import { getBYCEAnchorDisplayLabel, getBYCEConstructPresentation, selectBYCEVoiceAssessmentReference } from "../tools/byce/bycePresentation";
import resolveAudioUrl from "../../../utils/audio/resolveAudioUrl";

const entries = value => Object.entries(value || {}).filter(([, item]) => Number.isFinite(Number(item)));

function responsePresentation(task, responseKey) {
  const construct = (task.responseConfig?.constructs || []).find(candidate =>
    candidate.parameterId === responseKey || candidate.id === responseKey);
  const constructId = construct?.id || responseKey;
  const canonical = getBYCEConstructPresentation(constructId);
  return {
    constructId,
    parameterId: construct?.parameterId || responseKey,
    label: construct?.label || canonical.continuous?.sourceParameterLabel || canonical.label,
  };
}

function ReadOnlyResponseSummary({ responseRows, confidence }) {
  return <div className="av-completed-review-response__table-wrap">
    <table className="av-completed-review-response__table">
      <thead>
        <tr><th scope="col">Construct</th><th scope="col">Rating</th><th scope="col">Confidence</th></tr>
      </thead>
      <tbody>
        {responseRows.map(row => {
          const confidenceValue = confidence.find(item => item.constructId === row.constructId)?.value;
          return <tr key={row.parameterId}>
            <th scope="row">{row.constructLabel}</th>
            <td>{Math.round(Number(row.learnerValue))} / 100</td>
            <td>{Number.isFinite(Number(confidenceValue)) ? `${Math.round(Number(confidenceValue))} / 100` : "—"}</td>
          </tr>;
        })}
      </tbody>
    </table>
  </div>;
}

export default function CompletedProgramStepReview({ review, program, step, onDone }) {
  const [index, setIndex] = useState(0);
  const reviewTopRef = useRef(null);
  const previousIndexRef = useRef(index);
  useEffect(() => {
    if (previousIndexRef.current !== index) reviewTopRef.current?.scrollIntoView?.({ behavior: "auto", block: "start" });
    previousIndexRef.current = index;
  }, [index]);
  const trial = review.trials[index];
  const task = review.task;
  const capability = task.responseCapabilities?.expertBandFeedback;
  const variant = capability?.variants?.find(item => item.trialRole === trial?.governedTrialRole);
  const responseRows = useMemo(() => entries(trial?.response?.response).map(([responseKey, learnerValue]) => {
    const presentation = responsePresentation(task, responseKey);
    return {
      constructId: presentation.constructId,
      parameterId: presentation.parameterId,
      constructLabel: presentation.label,
      sourceParameterLabel: presentation.label,
      learnerValue,
      reference: selectBYCEVoiceAssessmentReference(trial?.feedback?.reference, presentation.parameterId),
    };
  }), [task, trial]);
  if (!trial) return <p>This completed-session review is unavailable.</p>;
  const target = trial.stimuli?.find(stimulus => ["primary", "target"].includes(stimulus.role)) || trial.stimuli?.[0];
  const confidence = trial.response?.learnerConfidence || [];
  const isReflection = task.responseType === "text";
  const presentationContract = program?.learnerPresentation?.feedbackBand;
  const presentationVariant = step?.phase === "stage-3" ? "stage3" : "ordinary";
  const showStandaloneTargetReplayInstruction = program?.learnerPresentation
    ?.listeningSessionPlacement?.standaloneFeedbackReplayInstruction?.visibility !== "hidden";
  const anchors = (review.anchors || []).map(anchor => ({ ...anchor, contentUrl: resolveAudioUrl(anchor.contentUrl) }));
  const singleConstructId = responseRows.length === 1
    ? responsePresentation(task, responseRows[0].parameterId).constructId
    : undefined;
  const anchorGroups = responseRows.length > 1
    ? review.anchorPresentation?.groups
    : undefined;

  return <main className="byce-task" data-runtime-mode="completed-review" ref={reviewTopRef}>
    <header className="byce-task__header">
      <p className="byce-task__eyebrow">Review completed session</p>
      <h2>{step.titleKey || task.title || "Completed session"}</h2>
      <p><strong>{review.lockedMessage}</strong></p>
      <p>You can replay the target voice and reference anchors, and revisit the feedback from this session.</p>
      <p>Voice {index + 1} of {review.trials.length}</p>
    </header>
    <section className="byce-task__surface av-completed-review-surface" aria-label={`Historical Voice ${index + 1}`}>
      {!isReflection && <TargetAudioPlayer sample={{ sampleLink: target?.contentUrl }} />}
      {isReflection ? <>
        <h3>Your submitted reflection</h3>
        <p>{trial.response?.response?.text}</p>
      </> : <>
        <section className="av-completed-review-response" aria-label="Your response">
          <h3>Your response</h3>
          <ReadOnlyResponseSummary responseRows={responseRows} confidence={confidence} />
        </section>
      </>}
      {trial.feedback?.state === "withheld" && <p role="status">Reference feedback remains unavailable for this Voice.</p>}
      {trial.feedback?.state === "released" && trial.feedback.ambiguityFeedback && <AmbiguityDistributionFeedback
        projection={trial.feedback.ambiguityFeedback}
        getConstructLabel={id => getBYCEConstructPresentation(id).label}
        primarySourceLabel="PVQD reference ratings"
      />}
      {trial.feedback?.state === "released" && trial.feedback.reference && capability && variant && <ExpertBandFeedback
        rows={responseRows}
        capability={capability}
        variant={variant}
        mirrorRows={trial.feedback.repeatedJudgmentMirror?.rows}
        presentationContract={presentationContract}
        presentationVariant={presentationVariant}
        headingHelp={<ExpertBandReferenceHelp />}
        showStandaloneTargetReplayInstruction={showStandaloneTargetReplayInstruction}
        targetReplay={null}
      />}
      {anchors.length > 0 && !isReflection && <ReferenceAnchorPlayer
        anchors={anchors}
        currentConstructId={singleConstructId}
        constructLabel={responseRows.length === 1 ? responseRows[0].constructLabel : undefined}
        headingLabel="Reference anchors"
        helpText="Use these reference sounds to revisit and calibrate your judgment."
        groups={anchorGroups}
        getDisplayLabel={getBYCEAnchorDisplayLabel}
      />}
      {!isReflection && review.linkedReflection && <section className="av-completed-review-reflection" aria-labelledby="completed-review-reflection-heading">
        <h3 id="completed-review-reflection-heading">Your reflection</h3>
        <p>{review.linkedReflection.text}</p>
      </section>}
    </section>
    <nav className="byce-task__actions" aria-label="Completed session Voices">
      <button className="app-btn app-btn-secondary" type="button" disabled={index === 0} onClick={() => setIndex(current => current - 1)}>Previous Voice</button>
      {index + 1 < review.trials.length
        ? <button className="app-btn app-btn-secondary" type="button" onClick={() => setIndex(current => current + 1)}>Next Voice</button>
        : <button className="app-btn app-btn-primary" type="button" onClick={onDone}>Back to Project</button>}
    </nav>
  </main>;
}

export { ReadOnlyResponseSummary, responsePresentation };
